/**
 * Image locks: who holds what, and clearing them.
 *
 * The lock is a single-writer hint with a TTL and no explicit release path, which is right for
 * contention and wrong for abandonment: a crashed tab holds a **live** lock on the annotator's
 * own image for the rest of the TTL, and until `POST /api/locks/clear` existed the only
 * recovery was to wait fifteen minutes or edit the database.
 *
 * This store deliberately does **not** wrap the confirmation. `confirm_release_live` is passed
 * in by the caller and defaults to nothing here, so no future caller can force-release a live
 * lock by accident: the flag has to be written out at a call site that a reviewer can see is
 * behind a prompt. The service refuses the request without it in any case.
 */

import { api } from '../api/client';
import { asProblem, type ApiProblem } from '../api/problem';
import type { ImageLockOut, LockClearResponse, LockInventory, LockScope } from '../api/types';
import { auth } from './auth.svelte';

class LockState {
  inventory = $state<LockInventory | null>(null);
  loading = $state(false);
  clearing = $state(false);
  problem = $state<ApiProblem | null>(null);
  /** The last clear, so the panel can report what actually happened rather than "done". */
  lastResult = $state<LockClearResponse | null>(null);

  get locks(): ImageLockOut[] {
    return this.inventory?.locks ?? [];
  }

  get total(): number {
    return this.inventory?.total ?? 0;
  }

  /** Live and stale are never summed. They mean different things to the person clearing. */
  get live(): number {
    return this.inventory?.live ?? 0;
  }

  get stale(): number {
    return this.inventory?.stale ?? 0;
  }

  /** Live locks held by somebody other than the caller — the ones with a person behind them. */
  get liveHeldByOthers(): number {
    return this.locks.filter((item) => item.lock.held && !item.held_by_caller).length;
  }

  /** Locked images by inscription, so the work queue can flag the rows it is showing. */
  get byInscription(): Map<number, ImageLockOut[]> {
    const map = new Map<number, ImageLockOut[]>();
    for (const item of this.locks) {
      if (item.inscription_id === null) continue;
      const list = map.get(item.inscription_id) ?? [];
      list.push(item);
      map.set(item.inscription_id, list);
    }
    return map;
  }

  async load(): Promise<void> {
    this.loading = true;
    this.problem = null;
    try {
      this.inventory = await api.locks();
    } catch (error) {
      this.problem = asProblem(error);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Release locks and reload.
   *
   * `confirmReleaseLive` is required at the call site rather than derived from `scope`: the
   * whole point of the second field is that two independent statements are needed, and a
   * store that filled in the second from the first would collapse them back into one.
   */
  async clear(options: {
    scope: LockScope;
    confirmReleaseLive: boolean;
    imageIds?: number[];
    reason?: string;
  }): Promise<LockClearResponse | null> {
    if (!auth.isAdmin) {
      // The service answers 403; saying so here means the operator finds out before the
      // confirmation dialog rather than after it.
      this.problem = asProblem(
        new Error('clearing locks is an admin operation: it can interrupt somebody mid-image'),
      );
      return null;
    }
    this.clearing = true;
    this.problem = null;
    try {
      const result = await api.clearLocks({
        // No `annotator_id`: who cleared a lock is the authenticated caller, recorded in the
        // service log. A self-declared name in that log would be worth nothing.
        scope: options.scope,
        confirm_release_live: options.confirmReleaseLive,
        ...(options.imageIds ? { image_ids: options.imageIds } : {}),
        ...(options.reason ? { reason: options.reason } : {}),
      });
      this.lastResult = result;
      await this.load();
      return result;
    } catch (error) {
      this.problem = asProblem(error);
      return null;
    } finally {
      this.clearing = false;
    }
  }

  dismissResult(): void {
    this.lastResult = null;
  }
}

export const locks = new LockState();
